/**
 * Typed API client. Every fetch to the backend goes through here — single
 * place to attach Clerk JWT, handle errors, log slow requests.
 *
 * Engineering Principle #2 (one front door): all backend calls funneled
 * through this module. No raw fetch() calls in React components.
 */

import { env } from "./env.js";

/**
 * App-wide query params merged into EVERY request, by SOURCE namespace:
 *   • "display"     — the gross↔net basis (refunds / tax / shipping), DisplayOptsProvider
 *   • "pageFilters" — the per-page right-rail filters (product / channel …), PageFiltersProvider
 * Each source owns its own keys (no collisions), so they merge cleanly. Only
 * NON-default keys are present, keeping URLs/caches clean. A per-call query
 * always overrides these.
 */
const globalQueryBySource: Record<string, Record<string, string | string[] | undefined>> = {};
export function setGlobalQuery(q: Record<string, string | string[] | undefined>, source = "display"): void {
  globalQueryBySource[source] = q;
}
export function clearGlobalQueryState(): void {
  for (const source of Object.keys(globalQueryBySource)) delete globalQueryBySource[source];
}
function mergedGlobalQuery(): Record<string, string | string[] | undefined> {
  return Object.assign({}, ...Object.values(globalQueryBySource));
}

export const EXPECTED_ACCOUNT_HEADER = "X-Expected-Account-ID";
export const ACTIVE_ACCOUNT_HEADER = "X-Active-Account-ID";

export interface ApiError {
  status: number;
  message: string;
  detail?: unknown;
  /** Seconds the server asked us to wait before retrying (from the Retry-After
   *  header on a 429/503). Lets react-query honor the server's backoff. */
  retryAfterMs?: number;
}

export interface ApiClientOptions {
  /** Returns the current Clerk session JWT, or null if unauthenticated. */
  getToken: (options?: { skipCache?: boolean }) => Promise<string | null>;
  /** Extra headers merged into every request — e.g. the public-demo marker. */
  extraHeaders?: Record<string, string>;
  /** Called on a 401 (expired/invalid session) so the app can recover instead
   *  of leaving the user clicking into generic error toasts forever. */
  onAuthError?: () => void;
  /** Called after the server proves this tab's account context is stale. The
   *  client freezes first, so the callback can clear state and reload without
   *  another request escaping under the newly active account. */
  onAccountContextChanged?: (activeAccountId: string | null) => void;
}

export class AccountContextChangedError extends Error {
  readonly status = 409;
  readonly code = "account_context_changed";

  constructor(readonly activeAccountId: string | null) {
    super("The active account changed. Reload required.");
  }
}

export class ApiClient {
  private readonly getToken: (options?: { skipCache?: boolean }) => Promise<string | null>;
  private readonly baseUrl: string;
  private readonly extraHeaders: Record<string, string>;
  private readonly onAuthError?: () => void;
  private readonly onAccountContextChanged?: (activeAccountId: string | null) => void;
  private expectedAccountId: string | null = null;
  private requestsFrozen = false;

  constructor(options: ApiClientOptions) {
    this.getToken = options.getToken;
    this.extraHeaders = options.extraHeaders ?? {};
    this.baseUrl = env.apiBaseUrl.replace(/\/+$/, "");
    this.onAuthError = options.onAuthError;
    this.onAccountContextChanged = options.onAccountContextChanged;
  }

  async get<T>(path: string, query?: Record<string, string | number | string[] | undefined>): Promise<T> {
    return this.call<T>("GET", path, { query });
  }

  /** One no-store request binds a fresh tab to exactly one active account. */
  async bindAccountContext<T>(path = "/account"): Promise<T> {
    return this.call<T>("GET", path, { accountContextMode: "handshake" });
  }

  async post<T>(path: string, body?: unknown): Promise<T> {
    return this.call<T>("POST", path, { body });
  }

  /** Public invitation resolution must stay unauthenticated even when the
   * browser already has a Clerk session. The one-time ticket stays in the POST
   * body and is never copied into a URL, referrer, or account-bound request. */
  async postPublic<T>(path: string, body: unknown): Promise<T> {
    const response = await fetch(this.buildUrl(path), {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
      cache: "no-store",
      credentials: "omit",
      referrerPolicy: "no-referrer",
    });
    if (!response.ok) {
      let detail: unknown;
      try {
        detail = await response.json();
      } catch {
        detail = await response.text();
      }
      throw {
        status: response.status,
        message: `POST ${path} failed: ${response.status}`,
        detail,
      } as ApiError;
    }
    if (response.status === 204) return undefined as T;
    return (await response.json()) as T;
  }

  /**
   * Invitation setup authenticates a newly created Clerk session before a
   * local user/account binding exists. This narrow request intentionally omits
   * account headers, cookies, credentials, global query parameters, and the
   * normal account-context machinery. Secret-bearing bodies remain confined to
   * this no-store POST and are never logged by this client.
   */
  async postInvitationAuth<T>(path: string, body: unknown): Promise<T> {
    const token = await this.getToken({ skipCache: true });
    if (!token) {
      throw { status: 401, message: "Invitation session is not available" } as ApiError;
    }
    const response = await fetch(this.buildUrl(path), {
      method: "POST",
      headers: {
        Accept: "application/json",
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
      cache: "no-store",
      credentials: "omit",
      referrerPolicy: "no-referrer",
    });
    if (!response.ok) {
      let detail: unknown;
      try {
        detail = await response.json();
      } catch {
        detail = await response.text();
      }
      const retryAfterHeader = response.headers.get("Retry-After");
      const retryAfterMs = retryAfterHeader && /^\d+$/.test(retryAfterHeader.trim())
        ? Number(retryAfterHeader.trim()) * 1000
        : undefined;
      throw {
        status: response.status,
        message: `POST ${path} failed: ${response.status}`,
        detail,
        ...(retryAfterMs !== undefined ? { retryAfterMs } : {}),
      } as ApiError;
    }
    if (response.status === 204) return undefined as T;
    return (await response.json()) as T;
  }

  /** The only request allowed to replace this tab's bound account context. */
  async postAccountChange<T>(path: string, body?: unknown): Promise<T> {
    return this.call<T>("POST", path, { body, accountContextMode: "change" });
  }

  async put<T>(path: string, body?: unknown): Promise<T> {
    return this.call<T>("PUT", path, { body });
  }

  async patch<T>(path: string, body?: unknown): Promise<T> {
    return this.call<T>("PATCH", path, { body });
  }

  async delete<T>(path: string): Promise<T> {
    return this.call<T>("DELETE", path);
  }

  /** Multipart POST (file uploads) — same auth, but the browser sets the
   *  multipart Content-Type boundary, so we never set it ourselves. No global
   *  query params are appended (this isn't a filterable data request). */
  async postForm<T>(path: string, form: FormData): Promise<T> {
    this.assertRequestsAllowed();
    const token = await this.getToken();
    if (token) this.assertAccountBound();
    const headers: Record<string, string> = { Accept: "application/json", ...this.extraHeaders };
    if (token) headers.Authorization = `Bearer ${token}`;
    if (token && this.expectedAccountId) headers[EXPECTED_ACCOUNT_HEADER] = this.expectedAccountId;
    this.assertRequestsAllowed();
    const response = await fetch(this.buildUrl(path), {
      method: "POST",
      headers,
      body: form,
      ...(token ? { cache: "no-store" as const } : {}),
    });
    await this.commitResponseAccountContext(response, Boolean(token), "normal");
    if (!response.ok) {
      let detail: unknown;
      try {
        detail = await response.json();
      } catch {
        detail = await response.text();
      }
      throw { status: response.status, message: `POST ${path} failed: ${response.status}`, detail } as ApiError;
    }
    if (response.status === 204) return undefined as T;
    return (await response.json()) as T;
  }

  private async call<T>(
    method: string,
    path: string,
    options: {
      query?: Record<string, string | number | string[] | undefined>;
      body?: unknown;
      accountContextMode?: "normal" | "handshake" | "change";
    } = {},
  ): Promise<T> {
    this.assertRequestsAllowed();
    const accountContextMode = options.accountContextMode ?? "normal";
    const token = await this.getToken();
    if (token && accountContextMode === "normal") this.assertAccountBound();
    const headers: Record<string, string> = {
      Accept: "application/json",
      ...this.extraHeaders,
    };
    if (token) headers.Authorization = `Bearer ${token}`;
    if (token && this.expectedAccountId) headers[EXPECTED_ACCOUNT_HEADER] = this.expectedAccountId;
    if (options.body !== undefined) headers["Content-Type"] = "application/json";

    const url = this.buildUrl(path, { ...mergedGlobalQuery(), ...(options.query ?? {}) });
    this.assertRequestsAllowed();
    const response = await fetch(url, {
      method,
      headers,
      body: options.body === undefined ? undefined : JSON.stringify(options.body),
      ...(token ? { cache: "no-store" as const } : {}),
    });
    await this.commitResponseAccountContext(response, Boolean(token), accountContextMode);

    if (!response.ok) {
      let detail: unknown;
      try {
        detail = await response.json();
      } catch {
        detail = await response.text();
      }
      const retryAfterHeader = response.headers.get("Retry-After");
      const retryAfterMs = retryAfterHeader && /^\d+$/.test(retryAfterHeader.trim())
        ? Number(retryAfterHeader.trim()) * 1000
        : undefined;
      const error: ApiError = {
        status: response.status,
        message: `${method} ${path} failed: ${response.status}`,
        detail,
        ...(retryAfterMs !== undefined ? { retryAfterMs } : {}),
      };
      if (response.status === 401 && this.onAuthError) this.onAuthError();
      throw error;
    }

    if (response.status === 204) return undefined as T;
    return (await response.json()) as T;
  }

  getExpectedAccountId(): string | null {
    return this.expectedAccountId;
  }

  freezeRequests(): void {
    this.requestsFrozen = true;
  }

  private assertRequestsAllowed(): void {
    if (this.requestsFrozen) throw new AccountContextChangedError(null);
  }

  private assertAccountBound(): void {
    if (this.expectedAccountId === null) this.failAccountContext(null);
  }

  private async commitResponseAccountContext(
    response: Response,
    authenticated: boolean,
    mode: "normal" | "handshake" | "change",
  ): Promise<void> {
    if (!authenticated) return;
    const responseAccountId = response.headers.get(ACTIVE_ACCOUNT_HEADER)?.trim() || null;

    if (response.status === 409) {
      const detail = await response.clone().json().catch(() => null) as { code?: unknown } | null;
      if (detail?.code === "account_context_changed") {
        this.failAccountContext(validAccountId(responseAccountId) ? responseAccountId : null);
      }
    }

    // Errors that happen before auth/bootstrap can legitimately have no account
    // context. Every successful authenticated response must have one.
    if (!validAccountId(responseAccountId)) {
      if (response.ok) this.failAccountContext(null);
      return;
    }
    if (mode === "change" && response.ok) {
      this.expectedAccountId = responseAccountId;
      return;
    }
    if (this.expectedAccountId === null && mode === "handshake") {
      this.expectedAccountId = responseAccountId;
      return;
    }
    if (responseAccountId !== this.expectedAccountId) {
      this.failAccountContext(responseAccountId);
    }
  }

  private failAccountContext(activeAccountId: string | null): never {
    this.expectedAccountId = null;
    this.requestsFrozen = true;
    this.onAccountContextChanged?.(activeAccountId);
    throw new AccountContextChangedError(activeAccountId);
  }

  private buildUrl(path: string, query?: Record<string, string | number | string[] | undefined>): string {
    const base = `${this.baseUrl}${path.startsWith("/") ? "" : "/"}${path}`;
    if (!query) return base;
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(query)) {
      if (value === undefined) continue;
      // Arrays become repeated params (?product=A&product=B) — multi-select filters.
      if (Array.isArray(value)) {
        for (const v of value) params.append(key, v);
        continue;
      }
      params.append(key, String(value));
    }
    const qs = params.toString();
    return qs ? `${base}?${qs}` : base;
  }
}

function validAccountId(value: string | null): value is string {
  return value !== null
    && /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value);
}
